import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vehicleService } from '../../services/vehicle.service';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { Vehicle, FuelType, ExpenseCategory } from '../../types';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';

const vehicleSchema = z.object({
  registrationNumber: z.string().min(1, 'Registration number is required'),
  make: z.string().min(1, 'Make is required'),
  model: z.string().min(1, 'Model is required'),
  year: z.number().min(2000, 'Year must be 2000 or later').max(new Date().getFullYear(), 'Year cannot be in the future'),
  fuelType: z.nativeEnum(FuelType),
  fuelEfficiencyKmPerLiter: z.number().positive('Fuel efficiency must be positive'),
  currentMileage: z.number().min(0, 'Mileage cannot be negative'),
  purchasingPrice: z.number().positive('Purchasing price must be positive').optional(),
});

type VehicleFormData = z.infer<typeof vehicleSchema>;

interface VehicleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle | null;
}

export const VehicleFormModal: React.FC<VehicleFormModalProps> = ({ isOpen, onClose, vehicle }) => {
  const queryClient = useQueryClient();
  const isEdit = !!vehicle;
  
  // LATRA/Insurance state (only for new vehicles)
  const [addLatra, setAddLatra] = useState(false);
  const [latraAmount, setLatraAmount] = useState('');
  const [latraValidFrom, setLatraValidFrom] = useState(new Date().toISOString().split('T')[0]);
  const [latraValidUntil, setLatraValidUntil] = useState('');
  const [latraAttachment, setLatraAttachment] = useState<File | null>(null);
  
  const [addInsurance, setAddInsurance] = useState(false);
  const [insuranceAmount, setInsuranceAmount] = useState('');
  const [insuranceValidFrom, setInsuranceValidFrom] = useState(new Date().toISOString().split('T')[0]);
  const [insuranceValidUntil, setInsuranceValidUntil] = useState('');
  const [insurancePolicyNumber, setInsurancePolicyNumber] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insuranceAttachment, setInsuranceAttachment] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: vehicle || {
      year: new Date().getFullYear(),
      fuelEfficiencyKmPerLiter: 0,
      currentMileage: 0,
    },
  });

  const createMutation = useMutation({
    mutationFn: vehicleService.create,
    onSuccess: async (createdVehicle) => {
      // Create LATRA expense if added
      if (addLatra && latraAmount) {
        try {
          const latraExpense = await vehicleExpenseService.recordExpense({
            vehicleId: createdVehicle.id,
            expenseDate: new Date().toISOString().split('T')[0],
            category: ExpenseCategory.LATRA,
            amount: parseFloat(latraAmount),
            validFrom: latraValidFrom || undefined,
            validUntil: latraValidUntil || undefined,
            description: 'LATRA registration',
          });
          
          // Upload attachment if provided
          if (latraAttachment) {
            try {
              await vehicleExpenseService.uploadAttachment(latraExpense.id, latraAttachment);
              toast.success('LATRA expense recorded with attachment');
            } catch (uploadErr) {
              console.error('Failed to upload LATRA attachment:', uploadErr);
              toast.success('LATRA expense recorded (attachment upload failed)');
            }
          } else {
            toast.success('LATRA expense recorded');
          }
        } catch (err: any) {
          console.error('Failed to create LATRA expense:', err);
          toast.error('Failed to record LATRA expense: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
        }
      }
      
      // Create Insurance expense if added
      if (addInsurance && insuranceAmount) {
        try {
          const insuranceExpense = await vehicleExpenseService.recordExpense({
            vehicleId: createdVehicle.id,
            expenseDate: new Date().toISOString().split('T')[0],
            category: ExpenseCategory.INSURANCE,
            amount: parseFloat(insuranceAmount),
            validFrom: insuranceValidFrom || undefined,
            validUntil: insuranceValidUntil || undefined,
            policyNumber: insurancePolicyNumber || undefined,
            insuranceProvider: insuranceProvider || undefined,
            description: 'Vehicle insurance',
          });
          
          // Upload attachment if provided
          if (insuranceAttachment) {
            try {
              await vehicleExpenseService.uploadAttachment(insuranceExpense.id, insuranceAttachment);
              toast.success('Insurance expense recorded with attachment');
            } catch (uploadErr) {
              console.error('Failed to upload Insurance attachment:', uploadErr);
              toast.success('Insurance expense recorded (attachment upload failed)');
            }
          } else {
            toast.success('Insurance expense recorded');
          }
        } catch (err: any) {
          console.error('Failed to create Insurance expense:', err);
          toast.error('Failed to record Insurance expense: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Vehicle created successfully');
      resetForm();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create vehicle');
    },
  });
  
  const resetForm = () => {
    reset();
    setAddLatra(false);
    setLatraAmount('');
    setLatraValidFrom(new Date().toISOString().split('T')[0]);
    setLatraValidUntil('');
    setLatraAttachment(null);
    setAddInsurance(false);
    setInsuranceAmount('');
    setInsuranceValidFrom(new Date().toISOString().split('T')[0]);
    setInsuranceValidUntil('');
    setInsurancePolicyNumber('');
    setInsuranceProvider('');
    setInsuranceAttachment(null);
  };

  const updateMutation = useMutation({
    mutationFn: (data: VehicleFormData) => vehicleService.update(vehicle!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Vehicle updated successfully');
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update vehicle');
    },
  });

  const onSubmit = (data: VehicleFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 1999 },
    (_, i) => ({
      value: 2000 + i,
      label: (2000 + i).toString(),
    })
  ).reverse();

  const fuelTypeOptions = Object.values(FuelType).map((type) => ({
    value: type,
    label: type,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        <Input
          label="Registration Number"
          placeholder="e.g., T123ABC"
          error={errors.registrationNumber?.message}
          {...register('registrationNumber')}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Make"
            placeholder="e.g., Toyota"
            error={errors.make?.message}
            {...register('make')}
            required
          />
          <Input
            label="Model"
            placeholder="e.g., Land Cruiser"
            error={errors.model?.message}
            {...register('model')}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Year"
            options={yearOptions}
            error={errors.year?.message}
            {...register('year', { valueAsNumber: true })}
            required
          />
          <Select
            label="Fuel Type"
            options={fuelTypeOptions}
            error={errors.fuelType?.message}
            {...register('fuelType')}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Fuel Efficiency (km/L)"
            type="number"
            step="0.1"
            placeholder="e.g., 10.5"
            error={errors.fuelEfficiencyKmPerLiter?.message}
            {...register('fuelEfficiencyKmPerLiter', { valueAsNumber: true })}
            required
          />
          <Input
            label="Current Mileage (km)"
            type="number"
            placeholder="e.g., 5000"
            error={errors.currentMileage?.message}
            {...register('currentMileage', { valueAsNumber: true })}
            required
          />
        </div>

        <Input
          label="Purchasing Price (TZS)"
          type="number"
          step="0.01"
          placeholder="e.g., 50000000"
          error={errors.purchasingPrice?.message}
          {...register('purchasingPrice', { valueAsNumber: true })}
        />

        {/* LATRA Section - Only for new vehicles */}
        {!isEdit && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="addLatra"
                checked={addLatra}
                onChange={(e) => setAddLatra(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="addLatra" className="font-medium text-gray-900">
                Add LATRA Registration
              </label>
            </div>
            {addLatra && (
              <div className="space-y-3 pl-6 border-l-2 border-orange-200">
                <Input
                  label="LATRA Amount (TZS)"
                  type="number"
                  value={latraAmount}
                  onChange={(e) => setLatraAmount(e.target.value)}
                  placeholder="e.g., 500000"
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Valid From"
                    type="date"
                    value={latraValidFrom}
                    onChange={(e) => setLatraValidFrom(e.target.value)}
                    required
                  />
                  <Input
                    label="Valid Until"
                    type="date"
                    value={latraValidUntil}
                    onChange={(e) => setLatraValidUntil(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attachment (PDF, JPG, PNG)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setLatraAttachment(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  {latraAttachment && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selected: {latraAttachment.name}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Insurance Section - Only for new vehicles */}
        {!isEdit && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="addInsurance"
                checked={addInsurance}
                onChange={(e) => setAddInsurance(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="addInsurance" className="font-medium text-gray-900">
                Add Vehicle Insurance
              </label>
            </div>
            {addInsurance && (
              <div className="space-y-3 pl-6 border-l-2 border-green-200">
                <Input
                  label="Insurance Amount (TZS)"
                  type="number"
                  value={insuranceAmount}
                  onChange={(e) => setInsuranceAmount(e.target.value)}
                  placeholder="e.g., 1000000"
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Valid From"
                    type="date"
                    value={insuranceValidFrom}
                    onChange={(e) => setInsuranceValidFrom(e.target.value)}
                    required
                  />
                  <Input
                    label="Valid Until"
                    type="date"
                    value={insuranceValidUntil}
                    onChange={(e) => setInsuranceValidUntil(e.target.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Policy Number"
                    value={insurancePolicyNumber}
                    onChange={(e) => setInsurancePolicyNumber(e.target.value)}
                    placeholder="e.g., INS-2024-001"
                  />
                  <Input
                    label="Insurance Provider"
                    value={insuranceProvider}
                    onChange={(e) => setInsuranceProvider(e.target.value)}
                    placeholder="e.g., NIC Insurance"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attachment (PDF, JPG, PNG)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setInsuranceAttachment(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  {insuranceAttachment && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selected: {insuranceAttachment.name}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
};
